import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Chip,
  Icon,
  ListGroup,
  ListRow,
  MoneyField,
  SegmentedControl,
  TextField,
} from '@uplink/ui';
import { useState } from 'react';
import { useDebounced } from '../admin/common.tsx';
import { errorText, useTRPC } from '../api.ts';
import { parseUsdInput } from '../format.ts';
import { MainActionBar } from '../native-controls.tsx';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { CopyText } from '../servers-ui.tsx';
import { haptic, openPayUrl } from '../telegram.ts';

type Kind = 'amount' | 'domain';
const PRESETS = [500, 1000, 2500, 5000];

/** Gift a friend an amount or a particular domain: paid from the balance, sent as a link. */
export function GiftScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { t, usd, date } = usePrefs();
  const balance = useQuery(trpc.balance.summary.queryOptions());
  const sent = useQuery(trpc.gifts.list.queryOptions());
  const [kind, setKind] = useState<Kind>('amount');
  const [amount, setAmount] = useState('10');
  const [domain, setDomain] = useState('');
  const [message, setMessage] = useState('');
  const [made, setMade] = useState<{ link: string | null } | null>(null);

  const typed = useDebounced(domain.trim().toLowerCase(), 450);
  const search = useQuery({
    ...trpc.domains.search.queryOptions({ query: typed, zone: 'popular' }),
    enabled: kind === 'domain' && /\.[a-z]{2,}$/.test(typed),
  });
  const found = search.data?.find((r) => r.domain === typed);
  const domainOk = found?.status === 'available' && found.price !== null;

  const cents = kind === 'amount' ? parseUsdInput(amount) : domainOk ? found!.price : null;
  const valid = !!cents && cents >= 100 && cents <= 100_000;
  const short = balance.data && cents ? Math.max(0, cents - balance.data.balanceCents) : 0;

  const refresh = () => {
    queryClient.invalidateQueries(trpc.gifts.pathFilter());
    queryClient.invalidateQueries(trpc.balance.pathFilter());
  };
  const create = useMutation(
    trpc.gifts.create.mutationOptions({
      onSuccess: (g) => {
        haptic.success();
        setMade({ link: g.link });
        refresh();
      },
      onError: () => haptic.error(),
    }),
  );
  const cancel = useMutation(trpc.gifts.cancel.mutationOptions({ onSuccess: refresh }));
  const share = (link: string) =>
    openPayUrl(
      `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(t.gift.shareText)}`,
    );

  return (
    <main className="ul-screen ul-screen--with-action">
      <h1 className="ul-screen__title">{t.gift.title}</h1>

      {made ? (
        <>
          <div className="ul-ready" role="status">
            <span className="ul-ready__mark" aria-hidden>
              <Icon name="gift" size={28} weight="fill" />
            </span>
            <span className="ul-ready__title">{t.gift.ready}</span>
            <span className="ul-ready__text">{t.gift.readyText}</span>
          </div>
          {made.link ? (
            <ListGroup>
              <ListRow
                icon="gift"
                title={made.link.replace(/^https:\/\//, '')}
                trailing={<CopyText text={made.link} label="" />}
              />
            </ListGroup>
          ) : null}
          <Button variant="ghost" onClick={() => setMade(null)}>
            {t.gift.another}
          </Button>
        </>
      ) : (
        <>
          <section className="ul-stack">
            <div className="ul-group__header">{t.gift.what}</div>
            <SegmentedControl<Kind>
              label={t.gift.what}
              value={kind}
              onChange={setKind}
              options={[
                { value: 'amount', label: t.gift.amount },
                { value: 'domain', label: t.gift.domain },
              ]}
            />
            {kind === 'amount' ? (
              <>
                <MoneyField id="gift-amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
                <div className="ul-chips">
                  {PRESETS.map((p) => (
                    <Chip key={p} selected={cents === p} onClick={() => setAmount(String(p / 100))}>
                      {usd(p).replace('.00', '')}
                    </Chip>
                  ))}
                </div>
              </>
            ) : (
              <TextField
                id="gift-domain"
                label={t.gift.domainLabel}
                value={domain}
                onChange={setDomain}
                placeholder="mybrand.com"
                hint={
                  found
                    ? domainOk
                      ? t.gift.domainFree(usd(found.price!))
                      : t.gift.domainTaken
                    : t.gift.domainHint
                }
                error={found && !domainOk ? t.gift.domainTaken : undefined}
              />
            )}
            <TextField
              id="gift-message"
              label={t.gift.message}
              value={message}
              onChange={setMessage}
              placeholder={t.gift.messagePlaceholder}
            />
          </section>
          <div className="ul-group__footer">{t.gift.footer}</div>
          {create.isError ? (
            <div className="ul-error" role="alert">
              {errorText(create.error)}
            </div>
          ) : null}
        </>
      )}

      {sent.data?.length ? (
        <ListGroup header={t.gift.sent}>
          {sent.data.map((g) => (
            <ListRow
              key={g.id}
              icon="gift"
              title={g.domain ?? usd(g.amountCents)}
              subtitle={`${date(g.createdAt)}${g.domain ? ` · ${usd(g.amountCents)}` : ''}`}
              trailing={
                g.status === 'active' ? (
                  <span className="ul-row__end">
                    <Badge tone="accent">{t.gift.statusActive}</Badge>
                    <span className="ul-gift-actions">
                      {g.link ? (
                        <button type="button" className="ul-link" onClick={() => share(g.link!)}>
                          {t.gift.share}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="ul-link ul-link--muted"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate({ id: g.id })}
                      >
                        {t.gift.cancel}
                      </button>
                    </span>
                  </span>
                ) : (
                  <Badge tone={g.status === 'claimed' ? 'ok' : 'neutral'}>
                    {g.status === 'claimed' ? t.gift.statusClaimed(g.claimerName) : t.gift.statusCancelled}
                  </Badge>
                )
              }
            />
          ))}
        </ListGroup>
      ) : null}

      <MainActionBar
        action={
          made
            ? made.link
              ? { text: t.gift.share, onClick: () => share(made.link!) }
              : null
            : !valid
              ? { text: t.gift.pick, onClick: () => {}, disabled: true }
              : short > 0
                ? {
                    text: t.gift.short(usd(Math.max(short, 500))),
                    onClick: () => nav.push({ name: 'topup', amountCents: Math.max(short, 500) }),
                  }
                : {
                    text: t.gift.give(usd(cents!)),
                    loading: create.isPending,
                    onClick: () =>
                      create.mutate({
                        amountCents: cents!,
                        domain: kind === 'domain' ? typed : undefined,
                        message: message.trim() || undefined,
                      }),
                  }
        }
      />
    </main>
  );
}
